/**
 * This file contains the customs types and functions about table endpoints
 * Ideally, we should be reading the type directly from arm-server.d.ts instead of maining it on FE
 * This is only possible when arm-server.d.ts is guaranteed to be up-to-date as per platform ASK
 * Platform Ask: https://webster.bfm.com/Wiki/display/AFWM/Platform+ASK
 */
import { SortOrder } from '@blk/aw-bff-typescript-models';
import { Column, Filter } from '@blk/awt-aladdinwealthserver';
import { AxiosResponse, CancelToken } from 'axios';
import moment from 'moment';
import { awApiWithPkg } from './Base';

/**
 * this interface defines metadata for any specific SubtotalRow, such as
 * - from which column did this subtotal row come from?
 * - what is the value of this subtotal row if we tried to sort it?
 * - how shall we display its value? (could be different than the value it is sorted on)
 */
export interface BucketInfo {
  colTag: string;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing usage
  value: any;
}

export interface RiskMonitorRow {
  bucketInfo: BucketInfo;
  // children can be null when singleChildResults is present in FetchRowsResponse
  children: RiskMonitorRow[] | null;
  collapsed: boolean;
  currentlySortedColumnNo?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing usage
  currentlySortedDirection?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing usage
  data: any;
  detail: boolean;
  detailRows: RiskMonitorRow[];
  errorStatus?: number;
  hidden: boolean;
  isSubtotal: boolean;
  loading: boolean;
  sectorPath: BucketInfo[];
  selected: boolean;
}

export interface FetchRowsRequestPayload {
  actionType?: string;
  groupBy?: Column;
  groupBys?: Column[]; // only applicable /fetch-rows-with-rollup
  nextGroupBys?: Column[]; // only applicable /fetch-rows-with-rollup
  nextSortBys?: InitialSortBy[][];
  preAggDetailRow?: boolean;
  purpose?: string;
  rowLimit?: number; // max threshold is 1000, defaults to 1000
  sectorPath: SectorPath[];
  select: Column[];
  sortBy?: InitialSortBy[];
  tableType?: string;
  useASL: boolean;
  useHive?: boolean;
}

export interface FetchRowsRequest {
  aggregateTag: string;
  analysisDate: string;
  benchType: string;
  filters: Filter[];
  payload: FetchRowsRequestPayload;
  pkg: string;
  /**
   * The today parameter is used to compute daysToDeadline and daysFromLastSnooze because both are derived fields calculated relative to the user’s current date.
   * If the parameter is not needed for a particular request, the backend will ignore it, so it's safe to always include it.
   * This parameter is optional; if not provided, the current date will be passed in by default.
   */
  today?: string;
}

export interface TopGroupsRequestPayload {
  groupBy: Column;
  rowLimit?: number; // max threshold is 20, default to 5
  select: Column[];
  sortBy?: InitialSortBy[];
}

export interface TopGroupsRequest {
  aggregateTag: string;
  analysisDate: string;
  benchType: string;
  filters: Filter[];
  payload: TopGroupsRequestPayload;
  pkg: string;
}

export interface InitialSortBy {
  colTag: string;
  direction: number;
}

export interface SortModelItem {
  colId: string;
  sort: SortOrder;
}

export interface SortBy {
  colTag: string;
  direction: number;
}

export interface SectorPath {
  colTag: string;
  title: string;
  value: string;
}

export interface FetchRowsResponse {
  detail: boolean;
  rows: RiskMonitorRow[];
  singleChildResults: FetchRowsResponse | null;
}

export interface TopGroupsResponse {
  rows: RiskMonitorRow[];
}

export function postFetchRowsWithRollUp(
  request: FetchRowsRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<FetchRowsResponse>> {
  const path = 'analysis/fetch-rows-with-rollup';
  return awApiWithPkg.post(
    path,
    { today: request.today ?? moment().format('YYYY-MM-DD'), ...request },
    { cancelToken, params: { pkg: request.pkg } }
  );
}

export function postFetchRows(
  request: FetchRowsRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<FetchRowsResponse>> {
  const path = 'analysis/fetch-rows';
  return awApiWithPkg.post(
    path,
    { today: request.today ?? moment().format('YYYY-MM-DD'), ...request },
    { cancelToken, params: { pkg: request.pkg } }
  );
}

export function postTopGroups(
  request: TopGroupsRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<TopGroupsResponse>> {
  const path = 'analysis/top-groups';
  return awApiWithPkg.post(path, request, { cancelToken, params: { pkg: request.pkg } });
}
